import React, { useState, useEffect } from 'react';
import { testcaseApi } from '../../../api/testcase';
import JiraTicketsPanel from './JiraTicketsPanel';
import JiraTicketModal from './JiraTicketModal';

const TestCaseDetailsModal = ({ testCase, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [testCaseDetails, setTestCaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  
  useEffect(() => {
    if (isOpen && testCase) {
      fetchTestCaseDetails();
    }
  }, [isOpen, testCase]);

  const fetchTestCaseDetails = async () => {
    if (!testCase || !testCase.test_case_id) return;
    
    setLoading(true);
    try {
      const response = await testcaseApi.getTestCaseById(testCase.test_case_id);
      setTestCaseDetails(response.test_case);
    } catch (err) {
      // Silently handle error and allow rendering with available data
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJiraTicket = () => {
    setJiraModalOpen(true);
  };

  const handleJiraTicketCreated = () => {
    // Refresh the Jira tickets data
    setActiveTab('jira');
  };

  if (!isOpen || !testCase) return null;

  const screenshots = testCase.screenshots ? JSON.parse(testCase.screenshots) : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8 mx-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        {/* Modal Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center bg-white z-10 sticky top-0">
          <h2 className="text-xl font-semibold text-gray-800">
            {testCase.test_case_details?.testname || 'Test Case Details'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Tabs */}
        <div className="border-b bg-white z-10 sticky top-[57px] flex-shrink-0">
          <nav className="flex -mb-px px-6">
            <button
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('details')}
            >
              Details & Screenshots
            </button>
            <button
              className={`py-4 px-6 font-medium text-sm border-b-2 flex items-center ${
                activeTab === 'jira'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('jira')}
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" title="Jira tickets">
                <path d="M12 2L4 7L12 12L20 7L12 2Z" fill={activeTab === 'jira' ? '#2684FF' : '#94A3B8'} />
                <path d="M4 7V17L12 22L20 17V7" stroke={activeTab === 'jira' ? '#2684FF' : '#94A3B8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Jira Tickets
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="overflow-y-auto flex-1" style={{ height: 'calc(100vh - 180px)' }}>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
            </div>
          ) : activeTab === 'details' ? (
            <div className="p-6">
              {/* Test Status */}
              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Test Status</h3>
                  <div className="flex items-center space-x-4">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${testCase.execution_status === 'completed' ? 'bg-green-100 text-green-800' : 
                        testCase.execution_status === 'failed' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'}`}
                    >
                      Execution: {testCase.execution_status}
                    </div>
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${testCase.test_case_status === 'passed' ? 'bg-green-100 text-green-800' : 
                        testCase.test_case_status === 'failed' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'}`}
                    >
                      Test: {testCase.test_case_status || 'pending'}
                    </div>
                  </div>
                </div>
                
                {/* Timing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Started</h3>
                    <p className="text-sm text-gray-900">
                      {testCase.started_at 
                        ? new Date(testCase.started_at).toLocaleString() 
                        : 'Not started'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Completed</h3>
                    <p className="text-sm text-gray-900">
                      {testCase.completed_at 
                        ? new Date(testCase.completed_at).toLocaleString() 
                        : 'Not completed'}
                    </p>
                  </div>
                </div>
                
                {/* Error Message */}
                {testCase.message && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Message</h3>
                    <div className="bg-gray-50 p-3 rounded-md overflow-x-auto">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                        {testCase.message}
                      </pre>
                    </div>
                  </div>
                )}
                
                {/* Test Case Details */}
                {testCaseDetails && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                      <p className="text-sm text-gray-900">
                        {testCaseDetails.description || 'No description provided'}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Assertion</h3>
                      <p className="text-sm text-gray-900">
                        {testCaseDetails.assertion || 'No assertion provided'}
                      </p>
                    </div>
                    
                    {testCaseDetails.steps && testCaseDetails.steps.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Steps</h3>
                        <div className="bg-gray-50 rounded-md">
                          <ul className="divide-y divide-gray-200">
                            {testCaseDetails.steps.map((step, index) => (
                              <li key={index} className="px-4 py-3 text-sm">
                                <div className="font-medium">{index + 1}. {step.action}</div>
                                {step.params && Object.keys(step.params).length > 0 && (
                                  <div className="mt-1 text-gray-600">
                                    {Object.entries(step.params).map(([key, value]) => (
                                      <div key={key} className="ml-4">
                                        - {key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Screenshots Section */}
              <div className="mt-10">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Screenshots</h3>
                {screenshots.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-md">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No screenshots</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      There are no screenshots available for this test run.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {screenshots.map((screenshot, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden shadow-sm">
                        <div className="p-3 bg-gray-50 border-b">
                          <p className="text-sm font-medium">{screenshot.step_name}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(screenshot.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <a 
                          href={screenshot.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img 
                            src={screenshot.url} 
                            alt={`Step: ${screenshot.step_name}`} 
                            className="w-full h-auto max-h-64 object-contain"
                            loading="lazy"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'jira' ? (
            <div className="px-6 py-4">
              <JiraTicketsPanel 
                testCaseId={testCase.test_case_id} 
                onCreateTicket={handleCreateJiraTicket}
                onTicketRemoved={handleJiraTicketCreated}
              />
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Jira Ticket Modal */}
      <JiraTicketModal
        isOpen={jiraModalOpen}
        onClose={() => setJiraModalOpen(false)}
        singleTestCaseId={testCase.test_case_id}
        testCaseDetails={{
          ...testCase.test_case_details,
          message: testCase.message
        }}
        onSuccess={handleJiraTicketCreated}
      />
    </div>
  );
};

export default TestCaseDetailsModal; 